import { formatNumber } from '@pmai/contracts';
import { cn } from './cn';

export interface TabOption<V extends string> {
  value: V;
  label: string;
  /** Shown as a counter next to the label. */
  count?: number;
  /** Draws attention (e.g. problems waiting) when the count is above 0. */
  highlight?: boolean;
}

/** Mutually exclusive views of one list (reconciliation tabs) — `tablist` with counters. */
export function Tabs<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V;
  options: readonly TabOption<V>[];
  onChange: (value: V) => void;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-1 border-b border-border">
      {options.map((o) => {
        const selected = o.value === value;
        const alert = o.highlight && (o.count ?? 0) > 0;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={selected}
            data-highlight={alert || undefined}
            onClick={() => onChange(o.value)}
            className={cn(
              '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium',
              selected
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-muted hover:text-ink',
              alert && !selected && 'text-amber-700',
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs tabular-nums',
                  alert ? 'bg-amber-100 text-amber-800' : 'bg-canvas text-ink-muted',
                )}
              >
                {formatNumber(o.count)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
